import type { SqlDriver, SqlRow, SqlValue } from "../../driver";
import type { OpenResult, WorkerRequest, WorkerResponse } from "./protocol";

/** The web driver: `SqlDriver` plus the lifecycle calls `openWebStore` needs. */
export interface WasmDriver extends SqlDriver {
  /** Install the OPFS SAH pool VFS and open the database named `name`. */
  open(name: string): Promise<OpenResult>;
  /** Close the database and delete its OPFS files. The worker stays alive; call `close()` after. */
  wipe(): Promise<void>;
}

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };
type Request = WorkerRequest extends infer R
  ? R extends WorkerRequest
    ? Omit<R, "id">
    : never
  : never;

/**
 * Main-thread proxy over `postMessage` to `worker.ts`. One request id per call; `batch`
 * is a single message. `close()` terminates the worker once the database is closed.
 */
export function createWasmDriver(worker: Worker): WasmDriver {
  let seq = 0;
  const pending = new Map<number, Pending>();
  let closed = false;

  worker.onmessage = (e: MessageEvent<WorkerResponse>) => {
    const msg = e.data;
    const p = pending.get(msg.id);
    if (!p) return;
    pending.delete(msg.id);
    if (msg.ok) p.resolve(msg.result);
    else p.reject(new Error(msg.error));
  };
  worker.onerror = (e: ErrorEvent) => {
    const err = new Error(e.message || "SQLite worker error");
    for (const p of pending.values()) p.reject(err);
    pending.clear();
  };

  function call<T>(req: Request): Promise<T> {
    if (closed) return Promise.reject(new Error("SQLite worker is closed"));
    const id = ++seq;
    return new Promise<T>((resolve, reject) => {
      pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      worker.postMessage({ id, ...req } as WorkerRequest);
    });
  }

  return {
    open: (name) => call<OpenResult>({ op: "open", name }),
    exec: (sql, params) =>
      call<undefined>(params ? { op: "exec", sql, params } : { op: "exec", sql }),
    query: <Row extends SqlRow = SqlRow>(sql: string, params?: SqlValue[]) =>
      call<Row[]>(params ? { op: "query", sql, params } : { op: "query", sql }),
    batch: (statements) => call<undefined>({ op: "batch", statements }),
    wipe: () => call<undefined>({ op: "wipe" }),
    async close() {
      if (closed) return;
      await call<undefined>({ op: "close" });
      closed = true;
      worker.terminate();
    },
  };
}
